/**
 * Pure logic for the WordBuilder exercise: decompose a target word into
 * akshara "slot groups", build a shuffled tray of tiles (needed units plus
 * seeded distractors), and check a learner's placement against the target.
 *
 * No halant tile exists: a cluster slot-group's consonants are shown in
 * order and the group itself (visually, one hanging box per akshara)
 * communicates that they're joined — the halant is implicit, never a
 * placeable unit.
 */
import type { Akshara, Word } from "@repo/hindi-shared";
import { letterById, parseAksharas, pickDistractors } from "@repo/hindi-shared";

/** A single draggable/clickable unit in the tile tray. */
export interface BuilderTile {
  id: string;
  label: string;
  kind: "consonant" | "matra" | "sign" | "vowel";
}

/** The parsed target plus the full (shuffled) tile tray for a WordBuilder round. */
export interface BuilderTarget {
  aksharas: Akshara[];
  tiles: BuilderTile[];
}

const DOTTED_CIRCLE = "◌";
const CONFUSABLE_CONSONANT_COUNT = 3;
const WRONG_MATRA_COUNT = 2;

/** Fisher-Yates shuffle using an injected `[0, 1)` rand source. Non-mutating. */
function shuffle<T>(items: readonly T[], rand: () => number): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    const tmp = copy[i]!;
    copy[i] = copy[j]!;
    copy[j] = tmp;
  }
  return copy;
}

/** Every matra letter id, for picking "wrong matra" distractors. */
function allMatraIds(): string[] {
  const ids: string[] = [];
  for (const letter of letterById.values()) {
    if (letter.kind === "matra") ids.push(letter.id);
  }
  return ids;
}

/** Consonant or independent-vowel glyph, for a "needed" tile. */
function glyphLabel(id: string): string {
  return letterById.get(id)?.glyph ?? id;
}

/** Matra or sign glyph rendered on a dotted-circle base, e.g. "◌ा". */
function dottedLabel(id: string): string {
  const glyph = letterById.get(id)?.glyph ?? "";
  return `${DOTTED_CIRCLE}${glyph}`;
}

let tileSeq = 0;
function makeTile(
  kind: BuilderTile["kind"],
  label: string,
  tag: string,
): BuilderTile {
  tileSeq += 1;
  return { id: `tile-${tag}-${tileSeq}`, label, kind };
}

/**
 * Build the needed tiles for a target's aksharas: one tile PER OCCURRENCE
 * of every consonant, matra, independent vowel, and sign the word actually
 * uses. Cluster consonants (e.g. न + द in हिन्दी) each get their own tile —
 * they are never merged into a single "cluster tile".
 */
function neededTiles(aksharas: readonly Akshara[]): BuilderTile[] {
  const tiles: BuilderTile[] = [];
  for (const akshara of aksharas) {
    if (akshara.other) continue; // punctuation/space/etc. never becomes a tile
    for (const consonantId of akshara.consonantIds) {
      tiles.push(makeTile("consonant", glyphLabel(consonantId), consonantId));
    }
    if (akshara.vowelId !== null) {
      tiles.push(
        makeTile("vowel", glyphLabel(akshara.vowelId), akshara.vowelId),
      );
    }
    if (akshara.matraId !== null) {
      tiles.push(
        makeTile("matra", dottedLabel(akshara.matraId), akshara.matraId),
      );
    }
    for (const signId of akshara.signIds) {
      if (signId === "sign-halant") continue; // implicit, never a tile
      tiles.push(makeTile("sign", dottedLabel(signId), signId));
    }
  }
  return tiles;
}

/** Every consonant id actually needed by the word (deduped), for picking a distractor anchor. */
function neededConsonantIds(aksharas: readonly Akshara[]): string[] {
  const ids = new Set<string>();
  for (const akshara of aksharas) {
    for (const id of akshara.consonantIds) ids.add(id);
  }
  return [...ids];
}

/** Every matra id actually needed by the word (deduped). */
function neededMatraIds(aksharas: readonly Akshara[]): Set<string> {
  const ids = new Set<string>();
  for (const akshara of aksharas) {
    if (akshara.matraId !== null) ids.add(akshara.matraId);
  }
  return ids;
}

/**
 * 2-3 confusable consonant distractors, anchored on a seeded pick among the
 * word's own needed consonants (so distractors "look like" something in the
 * word), then 1-2 wrong-matra distractors from matras the word doesn't use.
 * Distractor glyphs are always disjoint from the needed set so the "count
 * exactly once" invariant on needed tiles never gets accidentally satisfied
 * by a lookalike distractor.
 */
function distractorTiles(
  aksharas: readonly Akshara[],
  rand: () => number,
): BuilderTile[] {
  const tiles: BuilderTile[] = [];
  const needed = neededConsonantIds(aksharas);

  if (needed.length > 0) {
    const anchorIndex = Math.floor(rand() * needed.length);
    const anchor = needed[anchorIndex]!;
    // Over-fetch then filter so a confusable that's already in the word
    // (e.g. its own cluster partner) doesn't shrink the distractor count.
    const confusableIds = pickDistractors(
      anchor,
      CONFUSABLE_CONSONANT_COUNT + needed.length,
      rand,
    )
      .filter((id) => !needed.includes(id))
      .slice(0, CONFUSABLE_CONSONANT_COUNT);
    for (const id of confusableIds) {
      tiles.push(makeTile("consonant", glyphLabel(id), `distractor-${id}`));
    }
  }

  const usedMatraIds = neededMatraIds(aksharas);
  const unusedMatraIds = shuffle(
    allMatraIds().filter((id) => !usedMatraIds.has(id)),
    rand,
  ).slice(0, WRONG_MATRA_COUNT);
  for (const id of unusedMatraIds) {
    tiles.push(makeTile("matra", dottedLabel(id), `distractor-${id}`));
  }

  return tiles;
}

/**
 * Parse `word`, then build the full shuffled tile tray: every needed unit
 * (one tile per occurrence — cluster consonants included) plus 2-3
 * confusable-consonant distractors and 1-2 wrong-matra distractors.
 */
export function buildTarget(word: Word, rand: () => number): BuilderTarget {
  const aksharas = parseAksharas(word.devanagari).filter((a) => !a.other);
  const needed = neededTiles(aksharas);
  const distractors = distractorTiles(aksharas, rand);
  const tiles = shuffle([...needed, ...distractors], rand);
  return { aksharas, tiles };
}

/** The exact ordered label sequence a fully-correct slot group for `akshara` must match. */
function expectedLabels(akshara: Akshara): string[] {
  const labels: string[] = [];
  for (const consonantId of akshara.consonantIds) {
    labels.push(glyphLabel(consonantId));
  }
  if (akshara.vowelId !== null) {
    labels.push(glyphLabel(akshara.vowelId));
  }
  if (akshara.matraId !== null) {
    labels.push(dottedLabel(akshara.matraId));
  }
  for (const signId of akshara.signIds) {
    if (signId === "sign-halant") continue;
    labels.push(dottedLabel(signId));
  }
  return labels;
}

/**
 * Check whether `placed` (one array of tiles per akshara slot-group, in
 * akshara order) exactly matches `target`'s units: for each akshara, the
 * placed tiles' labels must equal — in order — that akshara's consonant
 * glyphs, then its independent-vowel glyph, then its matra sign, then its
 * signs. Any mismatch in count, order, or content anywhere fails the whole
 * check.
 */
export function checkComposition(
  target: Akshara[],
  placed: BuilderTile[][],
): boolean {
  if (placed.length !== target.length) return false;

  for (let i = 0; i < target.length; i += 1) {
    const akshara = target[i]!;
    const group = placed[i]!;
    const labels = expectedLabels(akshara);

    if (group.length !== labels.length) return false;
    for (let j = 0; j < labels.length; j += 1) {
      if (group[j]?.label !== labels[j]) return false;
    }
  }

  return true;
}
